// The dialogs: Merge, Move a pick ticket, Finish (gone from A2000), Add a list value, Manage list.

import { useRef, useState } from "react";
import { PlusIcon, RotateCcwIcon } from "lucide-react";
import {
  APP_STATUSES,
  ROUTING_STATUS_LIST,
  STATUS_CATEGORIES,
  cleanCarrier,
  cleanListValue,
  lookAlikes,
  sameListValue,
  statusCategory,
} from "@app/shared";
import type { AnyListName, LineView, ListValueRecord, StatusCategory } from "@app/shared";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useMessage } from "./message";
import { useDialogDrag } from "./dialog-drag";

function Radio({ checked, onChange, children }: { checked: boolean; onChange: () => void; children: React.ReactNode }) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm",
        checked && "border-primary bg-primary/5",
      )}
    >
      <input type="radio" className="accent-primary mt-1" checked={checked} onChange={onChange} />
      <span className="min-w-0 flex-1">{children}</span>
    </label>
  );
}

/** Lines whose pick tickets are all gone from A2000: shipped or cancelled? Not the Shipping workflow. */
export function FinishDialog({
  lines,
  onClose,
  onFinish,
}: {
  lines: LineView[];
  onClose: () => void;
  onFinish: (status: "Shipped" | "Cancelled", note: string) => void;
}) {
  const drag = useDialogDrag();
  const [status, setStatus] = useState<"Shipped" | "Cancelled">("Shipped");
  const [note, setNote] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent ref={drag.ref} style={drag.style} initialFocus={drag.ref} className="sm:max-w-md">
        <DialogHeader {...drag.handle}>
          <DialogTitle>Finish {lines.length === 1 ? "this line" : `${lines.length} lines`}</DialogTitle>
          <DialogDescription>
            Every pick ticket is gone from A2000. Say how {lines.length === 1 ? "it" : "they"} ended —{" "}
            {lines.length === 1 ? "it moves" : "they move"} to History.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Radio checked={status === "Shipped"} onChange={() => setStatus("Shipped")}>
            Shipped
          </Radio>
          <Radio checked={status === "Cancelled"} onChange={() => setStatus("Cancelled")}>
            Cancelled
          </Radio>
        </div>
        <Textarea
          placeholder="Note (optional)"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onFinish(status, note.trim())}>Finish</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Anyone adds; look-alikes first (spec §3.6, Figure 17). */
export function AddValueDialog({
  list,
  header,
  typed,
  values,
  manager,
  onClose,
  onUse,
  onAdd,
}: {
  list: AnyListName;
  header: string;
  typed: string;
  values: readonly ListValueRecord[];
  manager: boolean;
  onClose: () => void;
  onUse: (value: string) => void;
  onAdd: (value: string) => void;
}) {
  const drag = useDialogDrag();
  const field = useRef<HTMLInputElement>(null);
  const message = useMessage();
  const [text, setText] = useState(typed);
  const value = cleanListValue(text);
  const mine = values.filter((v) => v.list === list);
  const alike = value ? lookAlikes(value, mine) : [];
  const exact = mine.find((v) => v.value === value);
  const add = async () => {
    if (!value) return message.error("Nothing to add", "Type a value first.");
    if (exact) return onUse(exact.value);
    onAdd(value);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent ref={drag.ref} style={drag.style} initialFocus={field} className="sm:max-w-md">
        <DialogHeader {...drag.handle}>
          <DialogTitle>Add a value · {header}</DialogTitle>
          <DialogDescription>
            New values are added for everyone. To keep filters and reports clean, the tool checks for look-alike values
            first.
            {!manager ? " Only managers can edit, retire, delete or merge values later." : ""}
          </DialogDescription>
        </DialogHeader>
        <Input
          ref={field}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void add()}
        />
        {alike.length ? (
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Looks like a value that already exists:</p>
            {alike.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-sm"
              >
                <span className="truncate">
                  {v.value}
                  {v.retired === "Yes" ? <span className="text-muted-foreground"> (retired)</span> : null}
                </span>
                {v.retired !== "Yes" ? (
                  <Button size="sm" variant="secondary" onClick={() => onUse(v.value)}>
                    Use this
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void add()}>{alike.length && !exact ? "Add anyway" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Managers: add (from the search box, look-alikes first), edit (renames on every line), retire /
 * restore, delete (unused only), merge.
 */
export function ManageListDialog({
  list,
  header,
  values,
  usage,
  starting,
  onClose,
  onAdd,
  onCategory,
  onRename,
  onRetire,
  onDelete,
  onMerge,
  onLoadStarting,
}: {
  list: AnyListName;
  header: string;
  values: readonly ListValueRecord[];
  usage: ReadonlyMap<string, number>;
  starting: readonly string[] | undefined;
  onClose: () => void;
  /** Resolves true once it is saved. Routing status: with its category. */
  onAdd: (value: string, category?: StatusCategory) => Promise<boolean>;
  /** Routing status: a value's category changed. */
  onCategory?: (rec: ListValueRecord, category: StatusCategory) => void;
  onRename: (rec: ListValueRecord, value: string) => void;
  onRetire: (rec: ListValueRecord, retired: boolean) => void;
  onDelete: (rec: ListValueRecord) => void;
  onMerge: (keep: ListValueRecord, others: ListValueRecord[]) => void;
  onLoadStarting: () => void;
}) {
  const drag = useDialogDrag();
  const message = useMessage();
  // Routing status: each value has a category the rules read; the app's own two are never changed.
  const statuses = list === ROUTING_STATUS_LIST;
  const [newCategory, setNewCategory] = useState<StatusCategory>("Not routed");
  const appOwn = (v: ListValueRecord) => statuses && APP_STATUSES.includes(v.value);
  // Carriers and statuses keep their own case ("FedEx Freight", "Need info"); the other lists are upper case.
  const clean = statuses ? cleanCarrier : cleanListValue;
  const unit = "line(s)";
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [ticked, setTicked] = useState<string[]>([]);
  // One load at a time: a second click before the values arrive would race the first.
  const [loading, setLoading] = useState(false);
  const started = useRef(false);
  const [keep, setKeep] = useState("");
  const mine = values.filter((v) => v.list === list).sort((a, b) => a.value.localeCompare(b.value));
  const active = mine.filter(
    (v) => v.retired !== "Yes" && (!search || v.value.toLowerCase().includes(search.toLowerCase())),
  );
  const retired = mine.filter((v) => v.retired === "Yes");
  const used = (v: ListValueRecord) => usage.get(v.value) ?? 0;

  // The search box adds: a value not on the list yet (an exact match is there already, or restored).
  const typed = clean(search);
  const exact = sameListValue(typed, mine);
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!typed || exact || saving) return;
    // Look-alikes: what the search shows, and what the cell's "add a value" would warn about.
    const alike = [...new Set([...active, ...lookAlikes(typed, mine)])];
    if (
      alike.length &&
      !(await message.confirm(
        "Are you sure?",
        `“${typed}” looks like ${alike.length === 1 ? "a value" : "values"} already on the list. Use ${alike.length === 1 ? "it" : "one of them"} instead, or add it anyway.`,
        {
          lines: alike.map((v) => `${v.value}${v.retired === "Yes" ? " (retired)" : ""}`),
          confirmLabel: "Add anyway",
        },
      ))
    )
      return;
    setSaving(true);
    if (await onAdd(typed, statuses ? newCategory : undefined)) setSearch("");
    setSaving(false);
  };

  const saveEdit = async (rec: ListValueRecord) => {
    const value = clean(editing?.text ?? "");
    if (!value || value === rec.value)
      return message.error("Nothing to save", "Change the text first, then click Save edit.");
    if (mine.some((v) => v.value === value)) {
      return message.error(
        "Value already exists",
        `“${value}” is already on the list. To combine the two, tick both and use Merge.`,
      );
    }
    setEditing(null);
    onRename(rec, value);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        ref={drag.ref}
        style={drag.style}
        initialFocus={drag.ref}
        className="max-h-[90dvh] overflow-y-auto sm:max-w-xl"
      >
        <DialogHeader {...drag.handle}>
          <DialogTitle>Manage list · {header}</DialogTitle>
          <DialogDescription>
            Changes apply to everyone right away. A value used on lines can't be deleted — edit it, retire it, or merge
            it into another value.
          </DialogDescription>
        </DialogHeader>
        {mine.length === 0 && starting?.length ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-dashed p-2.5 text-sm">
            <span>
              {statuses
                ? `The ${starting?.length ?? 0} starting values are in use but not saved as a list yet. Load them to rename, retire or set their categories (adding a value loads them too).`
                : `This list is empty. Load its ${starting?.length ?? 0} starting values?`}
            </span>
            <Button
              size="sm"
              disabled={loading}
              onClick={() => {
                if (started.current) return;
                started.current = true;
                setLoading(true);
                onLoadStarting();
              }}
            >
              Load starting values
            </Button>
          </div>
        ) : null}
        <Input
          placeholder="Search or add a value"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            if (exact?.retired === "Yes") onRetire(exact, false);
            else void add();
          }}
        />
        {statuses ? (
          <label className="text-muted-foreground flex items-center gap-2 text-xs">
            A new value counts as
            <select
              className="text-foreground h-7 rounded-md border px-1"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value as StatusCategory)}
            >
              {STATUS_CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <span>— Routed counts as routed for the window, shipping and the Routed filter</span>
          </label>
        ) : null}
        {typed && !exact ? (
          <button
            type="button"
            disabled={saving}
            className="hover:bg-muted text-primary flex w-full items-center gap-2 rounded-md border border-dashed px-2.5 py-1.5 text-left text-sm font-medium disabled:opacity-50"
            onClick={() => void add()}
          >
            <PlusIcon className="size-4 shrink-0" />
            <span className="min-w-0 truncate">
              Add “{typed}”{statuses ? ` · ${newCategory}` : ""}
            </span>
          </button>
        ) : exact?.retired === "Yes" ? (
          <button
            type="button"
            className="hover:bg-muted text-primary flex w-full items-center gap-2 rounded-md border border-dashed px-2.5 py-1.5 text-left text-sm font-medium"
            onClick={() => onRetire(exact, false)}
          >
            <RotateCcwIcon className="size-4 shrink-0" />
            <span className="min-w-0 truncate">
              Restore “{exact.value}” <span className="text-muted-foreground font-normal">· it is retired</span>
            </span>
          </button>
        ) : null}
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left text-xs">
            <tr>
              <th className="w-8" />
              <th className="py-1">Value</th>
              {statuses ? <th className="py-1">Category</th> : null}
              <th className="py-1 text-right">Used on</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {active.map((v) => (
              <tr key={v.id} className="border-t">
                <td className="py-1.5">
                  <Checkbox
                    aria-label={`Tick ${v.value}`}
                    disabled={appOwn(v)}
                    checked={ticked.includes(v.id)}
                    onCheckedChange={(c) => setTicked((t) => (c ? [...t, v.id] : t.filter((x) => x !== v.id)))}
                  />
                </td>
                <td className="py-1.5 pr-2">
                  {editing?.id === v.id ? (
                    <Input
                      className="h-7"
                      autoFocus
                      value={editing.text}
                      onChange={(e) => setEditing({ id: v.id, text: e.target.value })}
                      // Enter saves, as Save edit does.
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        void saveEdit(v);
                      }}
                    />
                  ) : (
                    v.value
                  )}
                </td>
                {statuses ? (
                  <td className="py-1.5 pr-2">
                    <select
                      aria-label={`Category of ${v.value}`}
                      className="h-7 rounded-md border px-1 text-xs"
                      disabled={appOwn(v)}
                      value={statusCategory(v.value, values)}
                      onChange={(e) => onCategory?.(v, e.target.value as StatusCategory)}
                    >
                      {STATUS_CATEGORIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </td>
                ) : null}
                <td className="py-1.5 text-right whitespace-nowrap tabular-nums">
                  {used(v)} {unit}
                </td>
                <td className="py-1.5 pl-2 text-right whitespace-nowrap">
                  {appOwn(v) ? (
                    <span className="text-muted-foreground text-xs">Written by the app</span>
                  ) : editing?.id === v.id ? (
                    <Button size="sm" variant="secondary" onClick={() => void saveEdit(v)}>
                      Save edit
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ id: v.id, text: v.value })}>
                      Edit
                    </Button>
                  )}
                  {appOwn(v) ? null : used(v) > 0 ? (
                    <Button size="sm" variant="ghost" onClick={() => onRetire(v, true)}>
                      Retire
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-700"
                      onClick={() =>
                        void message
                          .confirm(`Delete “${v.value}”?`, "It leaves the list for good. No line uses it.", {
                            confirmLabel: "Delete",
                          })
                          .then((ok) => ok && onDelete(v))
                      }
                    >
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="space-y-1.5 border-t pt-2 text-sm">
          {ticked.length >= 2 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span>Merge {ticked.length} values into</span>
              <select className="h-8 rounded-md border px-2" value={keep} onChange={(e) => setKeep(e.target.value)}>
                <option value="">Pick the value to keep…</option>
                {mine
                  .filter((v) => ticked.includes(v.id))
                  .map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.value}
                    </option>
                  ))}
              </select>
              <Button
                size="sm"
                disabled={!keep}
                onClick={() => {
                  const target = mine.find((v) => v.id === keep)!;
                  onMerge(
                    target,
                    mine.filter((v) => ticked.includes(v.id) && v.id !== keep),
                  );
                  setTicked([]);
                  setKeep("");
                }}
              >
                Merge
              </Button>
              <p className="text-muted-foreground w-full text-xs">
                Every line with the other values moves to the kept value · the others disappear from the list.
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">
              Tick two or more look-alike values (e.g. CARTON LABEL, CARTON LABELS, CTN LABEL) to merge them.
            </p>
          )}
        </div>
        {retired.length ? (
          <div className="border-t pt-2 text-sm">
            <p className="text-muted-foreground mb-1 text-xs">Retired (kept on old lines):</p>
            <div className="flex flex-wrap gap-2">
              {retired.map((v) => (
                <span key={v.id} className="bg-muted rounded px-2 py-0.5">
                  {v.value} ·{" "}
                  <button type="button" className="text-primary hover:underline" onClick={() => onRetire(v, false)}>
                    restore
                  </button>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
